import { isDefined, isNotDefined } from '@sgme/fp';
import type { ColDef, ValueFormatterParams } from 'ag-grid-community';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';

import {
  AmountRenderer,
  DateRenderer,
  RateRenderer,
  StatusCellRenderer,
} from '@/App/Blotter/grids/commonCellRenderers.tsx';
import { basicTimeFormat, dateFormatterBuilder } from '@/App/Blotter/grids/commonFormatters.ts';
import {
  dateFilterDefinition,
  numberFilterDefinition,
  setFilterDefinition,
  textFilterDefinition,
} from '@/App/Blotter/grids/filterDefinitions.ts';
import { CommandsCellRenderer } from '@/App/Blotter/grids/option/CommandsCellRenderer';
import type { BlotterOptionEntry } from '@/store/blotter/types/BlotterOptionEntry.ts';
import type { ISODateTime } from '@/types/date.ts';

type ColumnDefinitions = {
  autoGroupColumnDef: ColDef<BlotterOptionEntry>;
  columnDefs: ColDef<BlotterOptionEntry>[];
};

export function useColumnDefinitions() {
  const intl = useIntl();
  return useMemo<ColumnDefinitions>(() => {
    const { formatMessage } = intl;

    const dateFormatter = dateFormatterBuilder<BlotterOptionEntry>(intl);

    return {
      autoGroupColumnDef: {
        field: 'id',
        headerName: formatMessage({ id: 'blotter.tab.Option.column.id' }),
        width: 170,
        lockPosition: true,
        ...textFilterDefinition,
      },
      columnDefs: [
        {
          colId: 'actions',
          valueGetter: ({ data }) => data?.id,
          headerName: formatMessage({ id: 'blotter.tab.Option.column.actions' }),
          cellRenderer: CommandsCellRenderer,
          width: 120,
          filter: false,
          sortable: false,
        },
        {
          field: 'account',
          headerName: formatMessage({ id: 'blotter.tab.Option.column.account' }),
          width: 200,
          ...textFilterDefinition,
        },
        {
          field: 'currencyPair',
          headerName: formatMessage({ id: 'blotter.tab.Option.column.currencyPair' }),
          width: 140,
          ...textFilterDefinition, // text filtering is more appropriate here rather than a set filter (possibly lots of different currency pairs in the grid)
        },
        {
          colId: 'tradeDate',
          headerName: formatMessage({ id: 'blotter.tab.Option.column.tradeDate' }),
          valueGetter: ({ data }) => data?.execTime,
          cellRenderer: DateRenderer,
          width: 180,
          ...dateFilterDefinition,
        },
        {
          field: 'expiry',
          headerName: formatMessage({ id: 'blotter.tab.Option.column.expiry' }),
          valueFormatter: ({ value, node, ...rest }: ValueFormatterParams<BlotterOptionEntry, ISODateTime>) =>
            // row groups emanating for multileg options has a meaningless expiry field
            (isDefined(node?.group) && node.group) || isNotDefined(value)
              ? ''
              : dateFormatter({ value, node, ...rest }),
          width: 120,
          ...dateFilterDefinition,
        },
        {
          field: 'dealtAmount',
          headerName: formatMessage({ id: 'blotter.tab.Option.column.dealtAmount' }),
          cellRenderer: AmountRenderer,
          width: 150,
          ...numberFilterDefinition,
        },
        {
          field: 'dealtCurrency',
          headerName: formatMessage({ id: 'blotter.tab.Option.column.dealtCurrency' }),
          width: 130,
          ...textFilterDefinition, // text filtering is more appropriate here rather than a set filter (possibly lots of different currencies in the grid)
        },
        {
          field: 'contraAmount',
          headerName: formatMessage({ id: 'blotter.tab.Option.column.contraAmount' }),
          cellRenderer: AmountRenderer,
          width: 170,
          ...numberFilterDefinition,
        },
        {
          field: 'contraCurrency',
          headerName: formatMessage({ id: 'blotter.tab.Option.column.contraCurrency' }),
          width: 170,
          ...textFilterDefinition, // text filtering is more appropriate here rather than a set filter (possibly lots of different currencies in the grid)
        },
        {
          field: 'portfolio',
          headerName: formatMessage({ id: 'blotter.tab.Option.column.portfolio' }),
          width: 120,
          ...textFilterDefinition,
        },
        {
          field: 'premium',
          headerName: formatMessage({ id: 'blotter.tab.Option.column.premium' }),
          width: 110,
          ...textFilterDefinition,
        },
        {
          field: 'product',
          headerName: formatMessage({ id: 'blotter.tab.Option.column.product' }),
          width: 100,
          valueFormatter: ({ value }) => {
            if (value === 'AmericanForward') {
              return formatMessage({ id: 'blotter.tab.Option.column.product.AmericanForward' });
            }

            return value;
          },
          ...setFilterDefinition,
        },
        {
          field: 'sales',
          headerName: formatMessage({ id: 'blotter.tab.Option.column.sales' }),
          width: 250,
          ...textFilterDefinition,
        },
        {
          field: 'spot',
          headerName: formatMessage({ id: 'blotter.tab.Option.column.spot' }),
          cellRenderer: RateRenderer,
          width: 120,
        },
        {
          field: 'status',
          headerName: formatMessage({ id: 'blotter.tab.Option.column.status' }),
          cellRenderer: StatusCellRenderer,
          width: 110,
          ...setFilterDefinition,
        },
        {
          field: 'strike',
          headerName: formatMessage({ id: 'blotter.tab.Option.column.strike' }),
          cellRenderer: AmountRenderer,
          width: 100,
          ...numberFilterDefinition,
        },
        {
          field: 'forwardRate',
          headerName: formatMessage({ id: 'blotter.tab.Option.column.forwardRate' }),
          cellRenderer: RateRenderer,
          width: 110,
        },
        {
          field: 'execTime',
          headerName: formatMessage({ id: 'blotter.tab.Option.column.execTime' }),
          valueGetter: ({ data }) => data?.execTime,
          valueFormatter: ({ value }: { value: ISODateTime }) => basicTimeFormat(value),
          width: 140,
          // no filter here : displayed times are not comparable because they (most probably) belong to different dates
        },
        {
          field: 'venue',
          headerName: formatMessage({ id: 'blotter.tab.Option.column.venue' }),
          width: 120,
          ...setFilterDefinition, // unsure, maybe text filter here, depending on the potential size of the set
        },
        {
          field: 'way',
          headerName: formatMessage({ id: 'blotter.tab.Option.column.way' }),
          width: 110,
          ...setFilterDefinition,
        },
        {
          field: 'premiumCurrency',
          headerName: formatMessage({ id: 'blotter.tab.Option.column.premiumCurrency' }),
          width: 160,
          ...textFilterDefinition, // text filtering is more appropriate here rather than a set filter (possibly lots of different currencies in the grid)
        },
        {
          field: 'premiumSide',
          headerName: formatMessage({ id: 'blotter.tab.Option.column.premiumSide' }),
          width: 140,
          ...setFilterDefinition,
        },
        {
          field: 'optionType',
          headerName: formatMessage({ id: 'blotter.tab.Option.column.optionType' }),
          width: 130,
          ...setFilterDefinition,
        },
        {
          field: 'electronicAccount',
          headerName: formatMessage({ id: 'blotter.tab.Option.column.electronicAccount' }),
          width: 230,
          ...textFilterDefinition,
        },
        {
          field: 'deliveryDate',
          headerName: formatMessage({ id: 'blotter.tab.Option.column.deliveryDate' }),
          valueFormatter: dateFormatter,
          width: 150,
          ...dateFilterDefinition,
        },
        {
          field: 'deliveryType',
          headerName: formatMessage({ id: 'blotter.tab.Option.column.deliveryType' }),
          width: 100,
          valueFormatter: ({ value }) => (isNotDefined(value) ? '' : value === 'Physical_Spot' ? 'Physical' : 'Cash'),
          ...setFilterDefinition,
        },
        {
          field: 'premiumPaymentDate',
          headerName: formatMessage({ id: 'blotter.tab.Option.column.premiumPaymentDate' }),
          valueFormatter: dateFormatter,
          width: 220,
          ...dateFilterDefinition,
        },
        {
          field: 'cutOffPlace',
          headerName: formatMessage({ id: 'blotter.tab.Option.column.cutOffPlace' }),
          width: 170,
          ...textFilterDefinition,
        },
        {
          field: 'callabilityStartDate',
          headerName: formatMessage({ id: 'blotter.tab.Option.column.callabilityStartDate' }),
          valueFormatter: dateFormatterBuilder(intl),
          width: 220,
          ...dateFilterDefinition,
        },
      ],
    };
  }, []);
}
